import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { getHs6pCodeListRequest } from 'redux/reducers/taxDuties/reducer';
import { getHs6pCodeList, getHs6pCodeListLoading } from 'redux/reducers/taxDuties/selectors';
import { THs6pCode, THs6pCodeListRequest } from 'redux/reducers/taxDuties/types';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { ETaxDutiesTabGroupLabel } from '../../constants';

export const Hs6pCodes: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);

	const list = useSelector(getHs6pCodeList);
	const listLoading = useSelector(getHs6pCodeListLoading);

	const params = useMemo(() => queryString.parse(search), [search]);

	const listRequest = useMemo<THs6pCodeListRequest>(() => {
		/**
		 * Every filter the table writes into the url is read back the same way the other sections do
		 * it, so a column only has to be filterable to reach the request. Reading from the default
		 * structure keeps the request out of reach of column visibility changes.
		 */
		const filters = getFiltersFromNavigateParams(defaultTableStructure, params) as TFilters;

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.ASC });

		return {
			pageNumber: params.page ? +params.page : 1,
			pageSize: PAGE_SIZE,
			searchString: String(params?.search ?? ''),
			filters,
			orderBy,
		};
	}, [params]);

	useEffect(() => {
		dispatch(getHs6pCodeListRequest(listRequest));
	}, [dispatch, listRequest]);

	/** Keeps only the visibility flags, mirroring the other reference tables. */
	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof THs6pCode>[]) => {
			setCurrentTableStructure((previous) =>
				previous.map((column) => {
					const changed = newTableStructure.find((item) => item.selector === column.selector);
					return changed ? { ...column, show: changed.show } : column;
				}),
			);
		},
		[],
	);

	return (
		<EasTableWithFilter<THs6pCode>
			tableStructure={currentTableStructure}
			items={list}
			itemsIsLoading={listLoading}
			handleAction={() => undefined}
			onTableStructureChange={handleTableStructureChange}
		>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Typography variant="body1" color="primary">
					Resources / Reference Tables / Tax &amp; Duties /{' '}
					<b>{ETaxDutiesTabGroupLabel.HS6P_CODES}</b>
				</Typography>
			</Box>
		</EasTableWithFilter>
	);
};
